import { GoogleGenerativeAI } from '@google/generative-ai';
import { SimulationConfig, WavePattern, Emitter, Point, AIWaveResponse } from '../components/WavePool/types';

// Initialize Gemini client
const genAI = new GoogleGenerativeAI(
  import.meta.env.VITE_GOOGLE_AI_KEY || ''
);

interface WavePatternGenerationRequest {
  description: string;
}

interface EmitterConfig {
  x: number;
  y: number;
  frequency: number;
  phase: number;
}

interface WaveGenerationResponse {
  name: string;
  description: string;
  emitters: EmitterConfig[];
  focusPoints?: { x: number; y: number }[];
  amplitude?: number;
  damping?: number;
  frequency?: number;
  loop?: boolean;
}

// Geometry helpers to keep emitters constrained to the real hardware (rim paddles)
const POOL_CENTER = 0.5;
const PERIMETER_RADIUS = 0.46; // Matches POOL_RADIUS/POOL_SIZE used by the renderer
const MIN_RADIUS_EPS = 1e-6;

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));

// Densify a polyline of focus points to improve phased-array fidelity
function densifyFocusPoints(points: Point[], maxSpacing = 0.015, maxPoints = 800) {
  if (points.length < 2) return points;

  const result: Point[] = [];
  const limit = Math.max(2, maxPoints);
  for (let i = 0; i < points.length; i++) {
    const a = points[i];
    const b = points[(i + 1) % points.length]; // close the shape

    const ax = clamp01(a.x);
    const ay = clamp01(a.y);
    const bx = clamp01(b.x);
    const by = clamp01(b.y);

    result.push({ x: ax, y: ay });

    const dx = bx - ax;
    const dy = by - ay;
    const dist = Math.hypot(dx, dy);
    const segments = Math.max(0, Math.ceil(dist / maxSpacing) - 1);

    for (let s = 1; s <= segments; s++) {
      if (result.length >= limit) break;
      const t = s / (segments + 1);
      result.push({ x: ax + dx * t, y: ay + dy * t });
    }

    if (result.length >= limit) break;
  }

  return result;
}

// Snap any coordinate to the circular rim where the 50 paddles live
function snapToPerimeter(x: number, y: number) {
  const dx = x - POOL_CENTER;
  const dy = y - POOL_CENTER;
  const mag = Math.sqrt(dx * dx + dy * dy);

  if (!isFinite(mag) || mag < MIN_RADIUS_EPS) {
    return {
      x: clamp01(POOL_CENTER + PERIMETER_RADIUS),
      y: POOL_CENTER,
    };
  }

  const scale = PERIMETER_RADIUS / mag;
  const px = POOL_CENTER + dx * scale;
  const py = POOL_CENTER + dy * scale;

  return {
    x: clamp01(px),
    y: clamp01(py),
  };
}

// Extract the first valid JSON object from a model response
function extractJsonString(raw: string): string {
  let text = raw.trim();

  // Strip code fences if present
  if (text.includes('```json')) {
    text = text.split('```json')[1]?.split('```')[0]?.trim() || text;
  } else if (text.includes('```')) {
    text = text.split('```')[1]?.split('```')[0]?.trim() || text;
  }

  // Fast path
  try {
    JSON.parse(text);
    return text;
  } catch {}

  // Balanced brace scan
  const len = text.length;
  let depth = 0;
  let start = -1;
  let inString = false;
  let escape = false;
  for (let i = 0; i < len; i++) {
    const ch = text[i];
    if (escape) {
      escape = false;
      continue;
    }
    if (ch === '\\') {
      escape = true;
      continue;
    }
    if (ch === '"') {
      inString = !inString;
      continue;
    }
    if (inString) continue;
    if (ch === '{') {
      if (depth === 0) start = i;
      depth++;
    } else if (ch === '}') {
      depth--;
      if (depth === 0 && start !== -1) {
        const candidate = text.slice(start, i + 1).trim();
        try {
          JSON.parse(candidate);
          return candidate;
        } catch {
          // keep scanning
        }
      }
    }
  }

  throw new SyntaxError('AI response did not contain valid JSON object.');
}

/**
 * Generates a wave pool simulation configuration from a natural language description
 * using Google's Gemini AI model.
 */
export async function generateWavePattern(
  prompt: string,
  options?: { loop?: boolean }
): Promise<AIWaveResponse> {
  if (!import.meta.env.VITE_GOOGLE_AI_KEY) {
    throw new Error(
      'Google AI API key not configured. Set VITE_GOOGLE_AI_KEY in your environment.'
    );
  }

  try {
    const model = genAI.getGenerativeModel({ model: 'gemini-2.0-flash' });

    const systemPrompt = `You are the control system for the AMOEBA circular wave pool simulator (50 paddles on the rim).

HARDWARE CONSTRAINTS:
- Paddles exist ONLY on the perimeter: radius ≈ 0.46, center at (0.5,0.5)
- Never place emitters inside the pool; all emitters must satisfy (x-0.5)^2 + (y-0.5)^2 ≈ 0.46^2
- Perimeter formula: x = 0.5 + 0.46*cos(angle), y = 0.5 + 0.46*sin(angle)

TWO MODES OF OPERATION:
1) Shape Printing (Phased Array)
   - Triggered by geometric/letter/icon prompts (e.g., star, heart, letter A, smiley, logo)
  - Output: focusPoints ONLY (dense point cloud outlining the shape). emitters MUST be an empty array
  - Density: spacing <= 0.015 between successive points; provide at least 80 points for simple shapes, 150+ for complex ones
  - Examples: square uses ~40 perimeter points; star uses many points along each arm

2) Atmospheric/Abstract (Direct Emitters)
   - Triggered by feelings/weather/abstract (e.g., storm, calm, chaos, breathing, resonance)
   - Output: emitters ONLY on the rim (use perimeter formula). focusPoints should be empty
   - Use 2-6 emitters with varied phase for interference; keep all on perimeter

JSON RESPONSE STRUCTURE:
{
  "name": "pattern name",
  "description": "scientific explanation",
  "emitters": [ {"x": 0-1, "y": 0-1, "frequency": 0.01-0.5, "phase": 0-6.28, "active": true} ],
  "focusPoints": [{"x": 0-1, "y": 0-1}],
  "amplitude": 0.5-2.0,
  "damping": 0.900-0.999,
  "frequency": 0.01-0.5
}

PARAMETER NOTES:
- frequency: 0.05-0.15 = broad motions, 0.2-0.4 = fine detail
- damping: 0.985-0.995 = slow fade, 0.96-0.98 = energetic decay
- amplitude: 0.5 subtle, 1.0 balanced, 1.5-2.0 dramatic
- focusPoints are targets for phased array solving (not emitter locations)

RULES:
- Return ONLY JSON, no markdown/code fences
- If shape mode: emitters = [], dense focusPoints
- If abstract mode: focusPoints = [], all emitters on perimeter
`;

    const loopHint =
      options?.loop === false
        ? 'Loop preference: NO LOOP (single burst to form the requested shape).'
        : 'Loop preference: LOOP ALLOWED (can repeat continuously).';

    const result = await model.generateContent({
      contents: [
        {
          role: 'user',
          parts: [
            {
              text: `${systemPrompt}\n\nUser request: "${prompt}"\n${loopHint}\n\nChoose the correct mode (shape vs abstract) and return valid JSON only.`,
            },
          ],
        },
      ],
      generationConfig: {
        temperature: 0.8,
        topK: 40,
        topP: 0.95,
        maxOutputTokens: 8000, // Increased to accommodate dense focus point arrays and prevent cutoff
      },
    });

    const responseText = result.response.text().trim();

    const jsonStr = extractJsonString(responseText);

    const waveConfig: WaveGenerationResponse = JSON.parse(jsonStr);

    // Validate and sanitize the response
    const emitters: Emitter[] = (waveConfig.emitters || [])
      .filter((e) => e && typeof e === 'object')
      .map((e) => {
        const snapped = snapToPerimeter(
          clamp01(e.x ?? POOL_CENTER + PERIMETER_RADIUS),
          clamp01(e.y ?? POOL_CENTER)
        );

        return {
          x: snapped.x,
          y: snapped.y,
          frequency: Math.max(0.01, Math.min(0.5, e.frequency || 0.2)),
          phase: (e.phase || 0) % (2 * Math.PI),
          active: true,
        };
      });

    // Ensure at least one emitter
    const focusPoints: Point[] | undefined =
      waveConfig.focusPoints && waveConfig.focusPoints.length > 0
        ? densifyFocusPoints(
            waveConfig.focusPoints
              .filter((p) => p && typeof p === 'object')
              .map((p) => ({
                x: clamp01(p.x ?? POOL_CENTER),
                y: clamp01(p.y ?? POOL_CENTER),
              }))
          )
        : undefined;

    // If neither mode produced emitters nor focus points, add a single rim emitter
    if (emitters.length === 0 && (!focusPoints || focusPoints.length === 0)) {
      const { x, y } = snapToPerimeter(
        POOL_CENTER + PERIMETER_RADIUS,
        POOL_CENTER
      );
      emitters.push({
        x,
        y,
        frequency: 0.2,
        phase: 0,
        active: true,
      });
    }

    const loopPref =
      options?.loop ?? (waveConfig as { loop?: boolean } | undefined)?.loop ?? true;

    const config: Partial<SimulationConfig> = {
      amplitude: Math.max(0.1, Math.min(2.0, waveConfig.amplitude || 1.0)),
      damping: Math.max(0.9, Math.min(0.999, waveConfig.damping || 0.985)),
      frequency: Math.max(0.01, Math.min(0.5, waveConfig.frequency || 0.2)),
      pattern: WavePattern.AI_GENERATED,
      autoPlay: true,
      rainMode: false,
      loop: loopPref,
    };

    return {
      name: waveConfig.name || 'AI Generated Pattern',
      description:
        waveConfig.description ||
        'Pattern generated by Gemini AI based on your description',
      config,
      emitters,
      focusPoints,
    };
  } catch (error) {
    if (error instanceof SyntaxError) {
      throw new Error(
        'AI response could not be parsed as JSON. The model may have returned text outside of the JSON object. Try a simpler or more specific description (e.g., "Star" instead of "Create a complex animated ocean scene").'
      );
    }
    if (error instanceof Error) {
      throw new Error(`AI generation failed: ${error.message}`);
    }
    throw new Error('Failed to generate wave pattern from AI');
  }
}

/**
 * Generates audio harmonics for Fourier Drawing based on a user prompt
 * Uses AI to describe what frequencies and phases would create the requested sound
 */
export async function generateFourierAudio(
  prompt: string
): Promise<{ coefficients?: DFTCoefficient[]; drawingPath?: number[]; description: string; baseHz?: number; brightnessTilt?: number; textureNoise?: number }> {
  if (!import.meta.env.VITE_GOOGLE_AI_KEY) {
    throw new Error(
      'Google AI API key not configured. Set VITE_GOOGLE_AI_KEY in your environment.'
    );
  }

  try {
    const model = genAI.getGenerativeModel({ model: 'gemini-2.0-flash' });

    const systemPrompt = `You are the AI sound designer for the Fourier Drawing Lab - a tool that creates waveforms.

For RHYTHMIC/PERCUSSIVE sounds (helicopter, heartbeat, drums), generate a DRAWING PATH.
For SUSTAINED/TONAL sounds (engines, bells, pads, wind), generate HARMONIC COEFFICIENTS.

DRAWING PATH MODE (rhythmic sounds):
Return JSON with either:
1. Full "drawingPath" array (512-1024 numbers), OR
2. Compressed format: "pattern" array (actual shape) + "totalLength" (512-1024)

The pattern represents ONE PERIOD of the sound.
Set baseHz to the rhythm speed (2-5 Hz for slow rhythms like helicopter).

CRITICAL: Pattern is ONE rhythmic unit that repeats at baseHz frequency.
- For 3 chops/sec helicopter: baseHz=3, pattern has ONE sharp peak+decay
- For 1.2 beats/sec heartbeat: baseHz=1.2, pattern has ONE lub-dub

Example for helicopter (compressed format - RECOMMENDED):
{
  "mode": "drawing",
  "description": "Single helicopter blade chop",
  "baseHz": 3,
  "brightnessTilt": -0.4,
  "textureNoise": 0.2,
  "pattern": [0.9, 0.85, 0.7, 0.5, 0.3, 0.15, 0.05, 0],
  "totalLength": 512
}
(System will pad with zeros to reach totalLength)

Pattern guide:
- Helicopter chop: Sharp spike (0→0.9 in 8-15 values), decay to 0. totalLength 512-1024. baseHz 2-4.
- Heartbeat: Two peaks (lub-dub) in 30-50 values. totalLength 512-1024. baseHz 1-1.5.
- Kick: Sharp attack (0→1→0) in 20-40 values. totalLength 512. baseHz 1-3.
- Keep pattern short (10-100 values), system pads with zeros to totalLength.

COEFFICIENT MODE (sustained sounds):
Return JSON with "coefficients" array.

Example for engine hum:
{
  "mode": "coefficients",
  "description": "Deep mechanical hum with overtones",
  "baseHz": 100,
  "brightnessTilt": -0.3,
  "textureNoise": 0.1,
  "coefficients": [
    {"freq": 1, "amp": 80, "phase": 0},
    {"freq": 2, "amp": 50, "phase": 120},
    {"freq": 3, "amp": 30, "phase": 240}
  ]
}

COEFFICIENT RULES:
- k=1 is the fundamental (highest amplitude 60-90)
- k is an integer 1-20. Higher k = brighter
- Amplitude 0-100. Phase 0-360°
- Include 6-12 harmonics
- Vary phases to avoid sterile tones

TIMBRE RECIPES (coefficient mode):

MECHANICAL/MOTOR SOUNDS:
- Engine/motor/idle: k=1(80), k=2(55), k=3(35), k=4(25), k=5(15), k=6(10). Phases: varied 30-300°. Heavy low harmonics, cap at k<=8. baseHz 80-120, textureNoise 0.1-0.15, brightnessTilt -0.4.
- Diesel rumble: k=1(85), k=2(60), k=3(40), k=4(28), k=5(18), k=6(12), k=7(8). Phases: 0, 45, 90, 135, 180, 225, 270. baseHz 70-100, textureNoise 0.12-0.18, brightnessTilt -0.5.
- Electric hum: k=1(85), k=2(10), k=4(8), k=6(5). Phases: all 0 (pure tone). baseHz 50 or 60, textureNoise 0.02, brightnessTilt -0.4.

TONAL/MELODIC SOUNDS:
- Bell/chime: k=1(55), k=2(50), k=3(40), k=5(45), k=7(35), k=9(28), k=12(22). Phases: stagger 0, 72, 144, 216, 288, 45, 135. baseHz 300-600, textureNoise 0.05, brightnessTilt 0.2.
- Warm pad/string: k=1(80), k=2(55), k=3(40), k=4(28), k=5(20), k=6(14). Phases: smooth 0, 30, 60, 90, 120, 150. baseHz 110-220, textureNoise 0.03, brightnessTilt -0.2.
- Brass/horn: k=1(75), k=2(60), k=3(50), k=4(40), k=5(32), k=6(25), k=7(18). Phases: 0, 60, 120, 180, 240, 300, 45. baseHz 140-220, textureNoise 0.06, brightnessTilt 0.1.

NOISE/TEXTURE SOUNDS:
- Wind/breath: k=1(35), k=3(32), k=5(28), k=7(25), k=9(20), k=11(16), k=13(12). Phases: random/varied. baseHz 150-250, textureNoise 0.2-0.25, brightnessTilt -0.1.
- Bright/metallic: k=1(65), k=2(45), k=3(38), k=8(50), k=10(42), k=12(38), k=15(32). Phases: 0, 180, 90, 45, 135, 225, 315. baseHz 200-400, textureNoise 0.08, brightnessTilt 0.5-0.7.

CRITICAL RULES:
- Return ONLY valid JSON (no text before/after, no code fences)
- Choose correct mode: drawing for rhythmic, coefficients for sustained
- For drawing mode: array must have 512-1536 numbers
- For coefficient mode: include 6-12 harmonics with k between 1-20`;

    const result = await model.generateContent({
      contents: [
        {
          role: 'user',
          parts: [
            {
              text: `${systemPrompt}\n\nUser request: "I want a sound that is: ${prompt}"\n\nGenerate appropriate DFT coefficients for this sound. Return only valid JSON.`,
            },
          ],
        },
      ],
      generationConfig: {
        temperature: 0.55,
        topK: 32,
        topP: 0.92,
        maxOutputTokens: 8000,
      },
    });

    const responseText = result.response.text().trim();
    const jsonStr = extractJsonString(responseText);

    interface FourierAudioResponse {
      mode?: 'drawing' | 'coefficients';
      description: string;
      coefficients?: DFTCoefficient[];
      drawingPath?: number[];
      pattern?: number[];
      totalLength?: number;
      baseHz?: number;
      brightnessTilt?: number;
      textureNoise?: number;
    }

    const config: FourierAudioResponse = JSON.parse(jsonStr);

    // Handle drawing mode
    if (config.mode === 'drawing') {
      let drawingPath: number[];
      
      // Support compressed format: pattern + totalLength
      if (config.pattern && Array.isArray(config.pattern) && config.totalLength) {
        const pattern = config.pattern
          .filter(v => typeof v === 'number' && isFinite(v))
          .map(v => Math.max(-1, Math.min(1, v)));
        
        const totalLength = Math.max(128, Math.min(2048, Math.floor(config.totalLength)));
        drawingPath = [...pattern];
        
        // Pad with zeros to reach totalLength
        while (drawingPath.length < totalLength) {
          drawingPath.push(0);
        }
      }
      // Support full drawingPath array
      else if (config.drawingPath && Array.isArray(config.drawingPath)) {
        drawingPath = config.drawingPath
          .filter(v => typeof v === 'number' && isFinite(v))
          .map(v => Math.max(-1, Math.min(1, v)))
          .slice(0, 2048);
      } else {
        throw new Error('Drawing mode requires either "pattern" + "totalLength" or "drawingPath" array');
      }

      if (drawingPath.length < 32) {
        throw new Error('Drawing path too short (need at least 32 samples)');
      }

      const clampNum = (val: unknown, min: number, max: number) => {
        if (val === undefined || val === null) return undefined;
        const num = Number(val);
        if (!isFinite(num)) return undefined;
        return Math.max(min, Math.min(max, num));
      };

      return {
        drawingPath,
        description: config.description || 'AI-generated waveform',
        baseHz: clampNum(config.baseHz, 0.1, 2000),
        brightnessTilt: clampNum(config.brightnessTilt, -1, 1),
        textureNoise: clampNum(config.textureNoise, 0, 0.25),
      };
    }

    // Handle coefficient mode
    const coefficients: DFTCoefficient[] = (config.coefficients || [])
      .filter((c) => c && typeof c === 'object' && c.freq >= 1 && c.freq <= 20)
      .slice(0, 15); // Limit to 15 harmonics

    // Ensure at least one coefficient
    if (coefficients.length === 0) {
      throw new Error('No valid coefficients or drawing path generated');
    }

    const clampNum = (val: unknown, min: number, max: number) => {
      if (val === undefined || val === null) return undefined;
      const num = Number(val);
      if (!isFinite(num)) return undefined;
      return Math.max(min, Math.min(max, num));
    };

    const sanitizedBaseHz = clampNum(config.baseHz, 0.1, 2000);
    const sanitizedBrightness = clampNum(config.brightnessTilt, -1, 1);
    const sanitizedTextureNoise = clampNum(config.textureNoise, 0, 0.25);

    return {
      coefficients: coefficients.map((c) => ({
        freq: Math.round(Math.max(1, Math.min(20, c.freq))),
        amp: Math.max(0, Math.min(100, c.amp ?? 50)),
        phase: ((c.phase ?? 0) % 360 + 360) % 360,
      })),
      description: config.description || 'AI-generated sound',
      baseHz: sanitizedBaseHz,
      brightnessTilt: sanitizedBrightness,
      textureNoise: sanitizedTextureNoise,
    };
  } catch (err) {
    const message =
      err instanceof Error
        ? err.message
        : 'Failed to generate audio from prompt';
    throw new Error(message);
  }
}

interface DFTCoefficient {
  freq: number;
  amp: number;
  phase: number;
}

/**
 * Validates if the Gemini API key is properly configured
 */
export function isGeminiConfigured(): boolean {
  return !!import.meta.env.VITE_GOOGLE_AI_KEY;
}

/**
 * Gets information about the AI service status
 */
export function getAIServiceStatus(): {
  configured: boolean;
  message: string;
} {
  if (isGeminiConfigured()) {
    return {
      configured: true,
      message: 'Gemini AI service is configured and ready',
    };
  }
  return {
    configured: false,
    message:
      'Gemini AI service requires VITE_GOOGLE_AI_KEY environment variable',
  };
}
